import { badgeStyle } from '../../utils/statusColors';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import BottomNav from '../../components/common/BottomNav';
import Avatar from '../../components/common/Avatar';
import ReviewModal from '../../components/common/ReviewModal';
import { InlineLoader } from '../../components/common/Loader';
import { getMyAppointments, getCancelInfo, cancelAppointment } from '../../services/patientService';
import { formatDate, formatTime12, formatCurrency, statusLabel } from '../../utils/helpers';

// ── Helper: check if an appointment's slot time has passed ───────────────
// slotTime is "HH:MM" (24hr), appointmentDate is "YYYY-MM-DD"
// Returns true if the slot start time is in the past
// Returns true if the appointment's own slot time has already passed (IST-aware)
// No buffer — cancel must happen BEFORE slot time, not after
function isSlotExpired(appt) {
  if (!appt.appointmentDate || !appt.slotTime) return false;
  try {
    const [h, m] = appt.slotTime.split(':').map(Number);
    // Build slot datetime in IST — appointmentDate is 'YYYY-MM-DD' (IST date)
    // We construct it as local midnight + hours:minutes so it matches IST display
    const slotDate = new Date(appt.appointmentDate + 'T00:00:00');
    slotDate.setHours(h, m, 0, 0);
    // Expired = slot time has passed (even by 1 second → cannot cancel)
    return Date.now() >= slotDate.getTime();
  } catch { return false; }
}

// ── Cancel Modal ──────────────────────────────────────────────────────────
function CancelModal({ appointmentId, onClose, onConfirm }) {
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getCancelInfo(appointmentId)
      .then(res => setInfo(res))
      .catch(() => { toast.error('Could not fetch cancel info'); onClose(); })
      .finally(() => setLoading(false));
  }, [appointmentId, onClose]);

  const doctorShare   = info?.doctorShare  || info?.cancellationCharge || 0;
  const adminShare    = info?.adminShare   || 0;
  const patientRefund = info?.refundAmount || 0;
  const totalPaid     = info?.totalPaid    || 0;
  // canCancel = false means slot time has already passed → block cancel
  const canCancel     = info?.canCancel !== false; // default true while loading

  const handleConfirm = async () => {
    setCancelling(true);
    try { await onConfirm(); }
    finally { setCancelling(false); }
  };

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="bottom-sheet p-5">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
        <h3 className="text-base font-bold text-text-primary font-heading mb-1">Cancel Appointment?</h3>
        <p className="text-xs text-text-muted mb-4">Here's exactly what will happen to your payment:</p>

        {/* Slot passed — show block message */}
        {!loading && info && !canCancel && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4 flex items-start gap-3">
            <i className="fa-solid fa-clock text-amber-500 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Cancellation Window Closed</p>
              <p className="text-xs text-amber-700 mt-1">
                Your slot time ({info.slotTime ? formatTime12(info.slotTime) : ''}) has already started.
                Appointments cannot be cancelled after the slot time begins.
              </p>
            </div>
          </div>
        )}

        {loading ? <InlineLoader text="Loading refund details..." /> : info && canCancel && (
          <div className="space-y-4">
            {/* Payment summary card */}
            <div className="rounded-2xl border border-border overflow-hidden">
              {/* Total Paid */}
              <div className="flex justify-between items-center px-4 py-3 bg-surface border-b border-border">
                <span className="text-sm text-text-secondary font-medium">Total Paid</span>
                <span className="text-sm font-bold text-text-primary">{formatCurrency(totalPaid)}</span>
              </div>

              {/* Refund to patient */}
              <div className="flex justify-between items-center px-4 py-3 bg-green-50 border-b border-green-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="fa-solid fa-arrow-rotate-left text-success text-xs" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-800">Your Refund</p>
                    <p className="text-[10px] text-green-600">Will be returned to your payment method</p>
                  </div>
                </div>
                <span className={`text-sm font-bold ${patientRefund > 0 ? 'text-success' : 'text-text-muted'}`}>
                  {patientRefund > 0 ? `+${formatCurrency(patientRefund)}` : '₹0'}
                </span>
              </div>

              {/* Doctor cancellation fee */}
              {doctorShare > 0 && (
                <div className="flex justify-between items-center px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center">
                      <i className="fa-solid fa-user-doctor text-amber-500 text-xs" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Doctor's Cancellation Fee</p>
                      <p className="text-[10px] text-text-muted">Set by doctor for cancellations</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-amber-600">−{formatCurrency(doctorShare)}</span>
                </div>
              )}

              {/* Platform fee */}
              {adminShare > 0 && (
                <div className="flex justify-between items-center px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center">
                      <i className="fa-solid fa-building-columns text-blue-500 text-xs" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Platform Fee</p>
                      <p className="text-[10px] text-text-muted">Non-refundable convenience + GST</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">−{formatCurrency(adminShare)}</span>
                </div>
              )}
            </div>

            {/* Refund note */}
            {patientRefund > 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-success text-lg flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    You'll receive {formatCurrency(patientRefund)} back
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">Refund processed to your original payment method in 5–7 business days</p>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <i className="fa-solid fa-circle-xmark text-danger text-lg flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">No refund applicable</p>
                  <p className="text-xs text-red-700 mt-0.5">Full amount retained as cancellation charge</p>
                </div>
              </div>
            )}

            <button onClick={handleConfirm} disabled={cancelling}
              className="btn-danger w-full py-3 rounded-btn flex items-center justify-center gap-2">
              {cancelling
                ? <><i className="fa-solid fa-spinner animate-spin" /> Cancelling...</>
                : <><i className="fa-solid fa-xmark mr-1" />Confirm Cancellation</>
              }
            </button>
            <button onClick={onClose} className="btn-outline w-full">Keep My Appointment</button>
          </div>
        )}
        {/* If slot passed — only show close button */}
        {!loading && info && !canCancel && (
          <button onClick={onClose} className="btn-primary w-full mt-2">Close</button>
        )}
      </div>
    </>
  );
}

// ── Appointment Card ──────────────────────────────────────────────────────
function AppointmentCard({ appt, onCancel, onReview }) {
  const navigate  = useNavigate();
  const doc       = appt.doctorId;

  // If slot time has passed and patient never checked in → treat as expired on UI
  const expired   = ['pending','confirmed'].includes(appt.status) && isSlotExpired(appt);
  // Video: also check if slot time has passed (even if not "expired" by general rules)
  // canCancel: status must be pending/confirmed AND appointment's own slot time must NOT have passed
  // isSlotExpired() checks exact slot time (no buffer) — same rule for clinic & video
  const canCancel = ['pending','confirmed'].includes(appt.status) && !expired;
  const canReview = appt.status === 'completed' && !appt.hasReview;

  return (
    <div className="bg-white rounded-2xl border border-border p-4 space-y-3 shadow-sm">
      {/* Doctor row */}
      <div className="flex items-center gap-3">
        <Avatar src={doc?.profileImg} name={doc?.name} size="sm" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{doc?.name || 'Doctor'}</p>
          <p className="text-xs text-primary truncate">{doc?.speciality}</p>
        </div>
        {expired
          ? <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600"><i className="fa-solid fa-clock-rotate-left text-[9px]" />Expired</span>
          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border" style={badgeStyle(appt.status)}>{statusLabel(appt.status)}</span>
        }
      </div>

      {/* Details */}
      <div className="flex items-center gap-3 text-xs text-text-muted border-t border-border pt-2 flex-wrap gap-y-1">
        <span><i className="fa-solid fa-calendar mr-1" />{formatDate(appt.appointmentDate)}</span>
        <span><i className="fa-solid fa-clock mr-1" />{formatTime12(appt.slotTime)}</span>
        <span className={`flex items-center gap-1 ${appt.type==='video'?'text-info':'text-primary'}`}>
          <i className={`fa-solid ${appt.type==='video'?'fa-video':'fa-hospital'} text-[10px]`} />
          {appt.type==='video'?'Video':'Clinic'}
        </span>
        <span className="ml-auto font-medium text-text-primary">Token #{appt.tokenNumber}</span>
      </div>

      {/* Patients */}
      {appt.patients?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {appt.patients.map((p, i) => (
            <span key={i} className="badge-gray text-[10px] px-2 py-0.5 rounded-full">
              {p.name} · {p.age}y{p.address ? ` · ${p.address}` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-1 flex-wrap">
        {/* View full appointment details — always visible */}
        <button onClick={() => navigate(`/appointments/${appt._id}`)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-surface border border-border rounded-lg text-xs font-semibold text-text-primary min-w-0">
          <i className="fa-solid fa-eye text-xs" />View Details
        </button>
        {appt.type === 'video' && ['confirmed','checked-in'].includes(appt.status) && (
          <button onClick={() => {
            const patientId = appt.patientId?._id || appt.patientId;
            const doctorId  = doc?._id || appt.doctorId?._id || appt.doctorId;
            if (!patientId || !doctorId) { toast.error('Cannot open chat'); return; }
            const convId = [patientId.toString(), doctorId.toString()].sort().join('_');
            navigate(`/chat/${convId}`, { state: { appointmentId: appt._id, doctorName: doc?.name, doctorImg: doc?.profileImg } });
          }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-info text-white rounded-lg text-xs font-semibold min-w-0">
            <i className="fa-solid fa-video text-xs" />Join Video
          </button>
        )}
        {appt.type === 'clinic' && (
          <button
            onClick={() => window.open(`https://maps.google.com?q=${doc?.location?.coordinates?.lat},${doc?.location?.coordinates?.lng}`, '_blank')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-border rounded-lg text-xs font-medium text-text-secondary min-w-0">
            <i className="fa-solid fa-location-dot text-primary text-xs" />Directions
          </button>
        )}
        {canReview && (
          <button onClick={() => onReview(appt)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs font-semibold text-amber-700 min-w-0">
            <i className="fa-solid fa-star text-amber-400 text-xs" />Rate Doctor
          </button>
        )}
        {appt.status === 'completed' && appt.hasReview && (
          <span className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-50 rounded-lg text-xs font-medium text-success">
            <i className="fa-solid fa-check text-xs" />Reviewed
          </span>
        )}
        {canCancel && (
          <button onClick={() => onCancel(appt._id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-danger/30 rounded-lg text-xs font-medium text-danger min-w-0">
            <i className="fa-solid fa-xmark text-xs" />Cancel
          </button>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: 'upcoming',  label: 'Upcoming',  icon: 'fa-calendar-check',  statuses: ['pending','confirmed','checked-in'] },
  { key: 'completed', label: 'Completed', icon: 'fa-circle-check',     statuses: ['completed'] },
  { key: 'cancelled', label: 'Cancelled', icon: 'fa-calendar-xmark',   statuses: ['cancelled'] },
];

export default function MyAppointmentsPage() {
  const [activeTab, setActiveTab]   = useState('upcoming');
  const [appointments, setAppts]    = useState([]);
  const [loading, setLoading]       = useState(true);
  const [cancelId, setCancelId]     = useState(null);
  const [reviewAppt, setReviewAppt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all appointments — no type filter, filter by status on frontend
      const res = await getMyAppointments({});
      setAppts(res.appointments || []);
    } catch {
      toast.error('Failed to load appointments. Please try again.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancelConfirm = async () => {
    try {
      const res = await cancelAppointment(cancelId);
      toast.success(res.message || 'Appointment cancelled');
      setCancelId(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const currentTab = TABS.find(t => t.key === activeTab);

  // Expired = pending/confirmed but slot time has passed without check-in
  // Show them in the "Cancelled" tab so patient knows they missed it
  const filtered = appointments.filter(a => {
    const expired = ['pending','confirmed'].includes(a.status) && isSlotExpired(a);
    if (activeTab === 'cancelled') {
      // Cancelled tab: actual cancelled + expired (missed) appointments
      return a.status === 'cancelled' || expired;
    }
    if (activeTab === 'upcoming') {
      // Upcoming: only non-expired pending/confirmed/checked-in
      return ['pending','confirmed','checked-in'].includes(a.status) && !expired;
    }
    return currentTab.statuses.includes(a.status);
  });

  const countFor = (key) => {
    if (key === 'cancelled') {
      return appointments.filter(a =>
        a.status === 'cancelled' ||
        (['pending','confirmed'].includes(a.status) && isSlotExpired(a))
      ).length;
    }
    if (key === 'upcoming') {
      return appointments.filter(a =>
        ['pending','confirmed','checked-in'].includes(a.status) && !isSlotExpired(a)
      ).length;
    }
    const tab = TABS.find(t => t.key === key);
    return appointments.filter(a => tab.statuses.includes(a.status)).length;
  };

  return (
    <div className="page-container">
      <Header title="My Appointments" showBack />

      {/* Status tabs */}
      <div className="sticky top-14 z-20 bg-white border-b border-border flex">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex-1 flex flex-col items-center py-2.5 text-xs font-semibold border-b-2 transition-all ${
              activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-text-muted'
            }`}>
            <i className={`fa-solid ${t.icon} text-sm mb-0.5`} />
            {t.label}
            {!loading && countFor(t.key) > 0 && (
              <span className={`text-[9px] font-bold mt-0.5 ${activeTab===t.key?'text-primary':'text-text-muted'}`}>
                ({countFor(t.key)})
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-4 pt-3 pb-4 space-y-3 max-w-2xl mx-auto">
        {loading ? <InlineLoader text="Loading appointments..." /> :
         filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-4 text-center">
            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center">
              <i className={`fa-solid ${currentTab.icon} text-text-muted text-2xl`} />
            </div>
            <div>
              <p className="font-semibold text-text-primary">
                No {activeTab} appointments
              </p>
              <p className="text-sm text-text-muted mt-1">
                {activeTab === 'upcoming' ? 'Book your first appointment' :
                 activeTab === 'completed' ? 'Completed appointments will appear here' :
                 'Cancelled appointments will appear here'}
              </p>
            </div>
          </div>
         ) : filtered.map(appt => (
          <AppointmentCard key={appt._id} appt={appt}
            onCancel={setCancelId}
            onReview={setReviewAppt} />
        ))}
      </div>

      {cancelId && (
        <CancelModal
          appointmentId={cancelId}
          onClose={() => setCancelId(null)}
          onConfirm={handleCancelConfirm} />
      )}

      {reviewAppt && (
        <ReviewModal
          appointment={reviewAppt}
          doctor={reviewAppt.doctorId}
          onClose={() => setReviewAppt(null)}
          onDone={load} />
      )}

      <BottomNav />
    </div>
  );
}
